/**
 * VerseSportAmbassadors — frontend-only engine for the World Cup
 * Ambassador Program. Backed by localStorage, derives signals from the
 * existing popularity engine when possible. No realtime, no backend.
 *
 * Phases 1, 3, 4, 5, 6, 7 of the Ambassador Program live here.
 */
import { useSyncExternalStore } from "react";
import { listPopularity, awardBadge, shareUserContent, type UserBadge } from "@/lib/pr24/popularityEngine";

export type AmbassadorRole =
  | "ambassador"
  | "national_captain"
  | "community_leader"
  | "creator_partner";

export type AmbassadorTier = "rookie" | "rising" | "featured" | "legend";

export interface AmbassadorRecord {
  id: string;
  handle: string;
  nation: string; // ISO-ish 2-3 letter code
  flag: string;
  role: AmbassadorRole;
  tier: AmbassadorTier;
  referrals: number;
  hype: number;
  shares: number;
  predictionScore: number;
  sponsor?: string | null;
  joinedAt: number;
}

export interface NationalCrew {
  nation: string;
  flag: string;
  name: string;
  members: number;
  hype: number;
  referrals: number;
  engagement: number;
  predictions: number;
  score: number;
}

export type MissionId =
  | "invite_5"
  | "nation_hype_dom"
  | "win_nation_war"
  | "share_3_clips"
  | "lead_predictions"
  | "referrals_10";

export interface Mission {
  id: MissionId;
  goal: number;
  reward: { title: string; badge: string; glow: string };
}

export interface MissionProgress {
  id: MissionId;
  progress: number;
  completed: boolean;
  completedAt?: number;
}

export const MISSIONS: Mission[] = [
  { id: "invite_5", goal: 5, reward: { title: "Connector", badge: "🤝", glow: "#22d3ee" } },
  { id: "nation_hype_dom", goal: 100, reward: { title: "Nation Hype King", badge: "👑", glow: "#facc15" } },
  { id: "win_nation_war", goal: 3, reward: { title: "War Champion", badge: "⚔️", glow: "#ef4444" } },
  { id: "share_3_clips", goal: 3, reward: { title: "Viral Engine", badge: "📣", glow: "#a855f7" } },
  { id: "referrals_10", goal: 10, reward: { title: "Recruiter", badge: "🚀", glow: "#10b981" } },
  { id: "lead_predictions", goal: 25, reward: { title: "Oracle Captain", badge: "🔮", glow: "#3b82f6" } },
];

const NATION_META: Record<string, { flag: string; name: string }> = {
  MEX: { flag: "🇲🇽", name: "Team México" },
  BRA: { flag: "🇧🇷", name: "Team Brasil" },
  ARG: { flag: "🇦🇷", name: "Team Argentina" },
  COL: { flag: "🇨🇴", name: "Team Colombia" },
  DOM: { flag: "🇩🇴", name: "Team RD" },
  USA: { flag: "🇺🇸", name: "Team USA" },
  ESP: { flag: "🇪🇸", name: "Team España" },
};

const K_AMB = "vs:ambassadors:v1";
const K_MISS = "vs:ambassadors:missions:v1";
const K_ME = "vs:ambassadors:me:v1";

const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());
const subscribe = (cb: () => void) => {
  listeners.add(cb);
  return () => listeners.delete(cb);
};

function read<T>(key: string, fallback: T): T {
  if (typeof window === "undefined") return fallback;
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}
function write<T>(key: string, value: T) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
    emit();
  } catch { /* noop */ }
}

function tierFor(score: number): AmbassadorTier {
  if (score >= 800) return "legend";
  if (score >= 400) return "featured";
  if (score >= 150) return "rising";
  return "rookie";
}

function seed(): AmbassadorRecord[] {
  const seedRows: Array<Partial<AmbassadorRecord> & { handle: string; nation: string; role: AmbassadorRole }> = [
    { handle: "@carlos_mx", nation: "MEX", role: "national_captain", referrals: 42, hype: 780, shares: 26, predictionScore: 58 },
    { handle: "@brasil_rey", nation: "BRA", role: "creator_partner", referrals: 31, hype: 690, shares: 22, predictionScore: 44 },
    { handle: "@luna_arg", nation: "ARG", role: "ambassador", referrals: 24, hype: 540, shares: 18, predictionScore: 51 },
    { handle: "@bogota_10", nation: "COL", role: "community_leader", referrals: 18, hype: 380, shares: 11, predictionScore: 22 },
    { handle: "@quisqueya", nation: "DOM", role: "ambassador", referrals: 12, hype: 240, shares: 7, predictionScore: 15 },
    { handle: "@miami_pro", nation: "USA", role: "creator_partner", referrals: 9, hype: 200, shares: 6, predictionScore: 11 },
  ];
  return seedRows.map((r, i) => {
    const score = (r.referrals ?? 0) * 6 + (r.hype ?? 0) * 0.2 + (r.shares ?? 0) * 4 + (r.predictionScore ?? 0) * 3;
    return {
      id: `amb-${i + 1}`,
      handle: r.handle,
      nation: r.nation,
      flag: NATION_META[r.nation]?.flag ?? "🌎",
      role: r.role,
      tier: tierFor(score),
      referrals: r.referrals ?? 0,
      hype: r.hype ?? 0,
      shares: r.shares ?? 0,
      predictionScore: r.predictionScore ?? 0,
      sponsor: r.sponsor ?? null,
      joinedAt: Date.now() - (i + 1) * 86_400_000,
    };
  });
}

export function listAmbassadors(): AmbassadorRecord[] {
  const cur = read<AmbassadorRecord[]>(K_AMB, []);
  if (cur.length) return cur;
  const fresh = seed();
  write(K_AMB, fresh);
  return fresh;
}

export function ambassadorScore(a: AmbassadorRecord): number {
  return Math.round(a.referrals * 6 + a.hype * 0.2 + a.shares * 4 + a.predictionScore * 3);
}

export function rankedAmbassadors(limit = 20): AmbassadorRecord[] {
  return [...listAmbassadors()]
    .sort((a, b) => ambassadorScore(b) - ambassadorScore(a))
    .slice(0, limit);
}

/** Aggregate ambassadors + popularity engine signals per nation. */
export function getNationalCrews(): NationalCrew[] {
  const ambs = listAmbassadors();
  const pop = listPopularity();
  const map = new Map<string, NationalCrew>();
  for (const a of ambs) {
    const meta = NATION_META[a.nation] ?? { flag: a.flag, name: a.nation };
    const cur = map.get(a.nation) ?? {
      nation: a.nation, flag: meta.flag, name: meta.name,
      members: 0, hype: 0, referrals: 0, engagement: 0, predictions: 0, score: 0,
    };
    cur.members += 1;
    cur.hype += a.hype;
    cur.referrals += a.referrals;
    cur.engagement += a.shares * 2;
    cur.predictions += a.predictionScore;
    map.set(a.nation, cur);
  }
  for (const p of pop) {
    const code = (p.team ?? "").toUpperCase();
    if (!code || !NATION_META[code]) continue;
    const cur = map.get(code);
    if (!cur) continue;
    cur.members += 1;
    cur.hype += p.signals.likes_received + p.signals.messages_sent;
    cur.referrals += p.signals.referrals;
    cur.engagement += p.signals.shares * 2 + p.signals.surveys_answered;
    cur.predictions += p.signals.predictions_correct;
  }
  const crews = [...map.values()].map((c) => ({
    ...c,
    score: Math.round(c.hype * 0.25 + c.referrals * 8 + c.engagement * 1.5 + c.predictions * 2),
  }));
  return crews.sort((a, b) => b.score - a.score);
}

// --- Self ambassador (the current viewer) ---

export interface MeAmbassador {
  handle: string;
  nation: string;
  referralCode: string;
  isAmbassador: boolean;
}

function randomCode() {
  const a = "ABCDEFGHJKMNPQRSTUVWXYZ23456789";
  let s = "VS";
  for (let i = 0; i < 6; i++) s += a[Math.floor(Math.random() * a.length)];
  return s;
}

export function getMe(): MeAmbassador {
  return read<MeAmbassador>(K_ME, {
    handle: "@you",
    nation: "MEX",
    referralCode: randomCode(),
    isAmbassador: false,
  });
}
export function joinProgram(input: { handle: string; nation: string }) {
  const me = { ...getMe(), ...input, isAmbassador: true };
  write(K_ME, me);
}
export function setNation(nation: string) {
  write(K_ME, { ...getMe(), nation });
}

// --- Missions ---

export function getMissionProgress(): MissionProgress[] {
  const stored = read<MissionProgress[]>(K_MISS, []);
  const byId = new Map(stored.map((m) => [m.id, m]));
  return MISSIONS.map((m) =>
    byId.get(m.id) ?? { id: m.id, progress: 0, completed: false },
  );
}

export function bumpMission(id: MissionId, delta = 1) {
  const list = getMissionProgress().map((m) => ({ ...m }));
  const target = list.find((m) => m.id === id);
  if (!target) return;
  const def = MISSIONS.find((d) => d.id === id)!;
  target.progress = Math.min(def.goal, target.progress + delta);
  const justCompleted = !target.completed && target.progress >= def.goal;
  if (justCompleted) {
    target.completed = true;
    target.completedAt = Date.now();
  }
  write(K_MISS, list);
  if (justCompleted) {
    // Wire mission completion → existing PR-24 achievements/badges.
    // No new logic: reuses awardBadge + shareUserContent already exposed by
    // popularityEngine, so AchievementsCenter (titles/badges) reflects it.
    const MISSION_BADGE: Record<MissionId, UserBadge> = {
      invite_5: "verified_fan",
      referrals_10: "brand_ambassador",
      share_3_clips: "brand_ambassador",
      nation_hype_dom: "streak_30",
      win_nation_war: "prediction_expert",
      lead_predictions: "prediction_expert",
    };
    try {
      awardBadge("seed-1", MISSION_BADGE[id]);
      shareUserContent("seed-1", "ranking", { mission: id });
    } catch { /* noop */ }
  }
}

// --- React glue ---

function snapshot() {
  return { ambassadors: listAmbassadors(), missions: getMissionProgress(), me: getMe() };
}
let cache = snapshot();
let cacheKey = "";
const refresh = () => {
  cache = snapshot();
  cacheKey = `${cache.ambassadors.length}:${cache.missions.map((m) => m.progress).join(",")}:${cache.me.isAmbassador ? 1 : 0}:${cache.me.nation}`;
};
refresh();
listeners.add(refresh);

export function useAmbassadorSnapshot() {
  return useSyncExternalStore(
    subscribe,
    () => { void cacheKey; return cache; },
    () => cache,
  );
}

// --- Sponsor synergy (lightweight registry) ---

export interface NationSponsor {
  nation: string;
  brand: string;
  hex: string;
  tagline: string;
}

export const NATION_SPONSORS: NationSponsor[] = [
  { nation: "MEX", brand: "Caliente", hex: "#FFCC29", tagline: "Apoyando a Team México" },
  { nation: "BRA", brand: "Betano", hex: "#16a34a", tagline: "Powering Team Brasil" },
  { nation: "ARG", brand: "Codere", hex: "#60a5fa", tagline: "Vamos Argentina" },
  { nation: "COL", brand: "BetPlay", hex: "#f97316", tagline: "Fuerza Colombia" },
];

export function sponsorForNation(nation: string): NationSponsor | null {
  return NATION_SPONSORS.find((s) => s.nation === nation) ?? null;
}
